import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  X, Zap, Bot, Brain, Car, Wrench, 
  BarChart3, FileText, Package, Users, Cat, Sparkles, ShoppingBasket, Plus, Mail, Globe,
  Home // Standardized branding icon
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

export const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const corePlans = [
    {
      id: 'free',
      name: t('pricing.free_name', 'Free'),
      tagline: t('pricing.free_tag', 'Get Organized, Simply'),
      price: '$0',
      icon: '🌱',
      featureIcon: '✅',
      features: [
        "1 household", 
        "Up to 2 household members", 
        "Shared task list (20 tasks)", 
        "Shared calendar (30-days)", 
        "Grocery lists (20 items)", 
        "Basic reminders"
      ],
      notIncluded: ["Advanced automations", "Usage-based maintenance", "Historical analytics", "Priority support"],
      buttonText: t('nav.get_started', 'Get Started'),
      accent: 'border-slate-200 dark:border-slate-800'
    },
    {
      id: 'basic',
      name: t('pricing.basic_name', 'Basic'),
      tagline: t('pricing.basic_tag', 'Everyday, Upgraded'),
      price: '$4.99',
      icon: '🌿',
      featureIcon: '⭐',
      recommended: true,
      features: [
        "Up to 6 household members", 
        "Up to 2 households", 
        "Advanced maintenance scheduling", 
        "Task & event history (1-year)", 
        "File uploads (receipts, photos)", 
        "Feature permissions per user", 
        "Basic support"
      ],
      notIncluded: ["Smart automations", "AI-generated tasks", "Long-term analytics"],
      buttonText: t('nav.get_started', 'Get Started'),
      accent: 'border-indigo-600 ring-4 ring-indigo-600/10'
    },
    {
      id: 'premium',
      name: t('pricing.premium_name', 'Premium'),
      tagline: t('pricing.premium_tag', 'Fully Automated Living'),
      price: '$12.99',
      icon: '🌳',
      featureIcon: '🚀',
      features: [
        "Up to 8 household members", 
        "Up to 4 households", 
        "Smart automations", 
        "AI-generated tasks & reminders", 
        "Full activity history", 
        "Advanced insights & forecasts", 
        "Priority support"
      ],
      notIncluded: [],
      buttonText: t('nav.get_started', 'Get Started'),
      accent: 'border-amber-500 shadow-xl shadow-amber-500/10'
    }
  ];

  const addOns = [
    { icon: Bot, name: "Smart Automations", desc: "Create tasks based on weather, mileage, and activity." },
    { icon: Brain, name: "AI Task Assistant", desc: "Generate tailored schedules based on household habits." },
    { icon: Car, name: "Vehicle Intelligence", desc: "Proactive reminders tied to real usage and predicted needs." },
    { icon: Wrench, name: "Advanced Maintenance", desc: "Unlock detailed history and compliance tracking." },
    { icon: BarChart3, name: "Insights & Analytics", desc: "Visualize activity trends and maintenance health." },
    { icon: FileText, name: "File Storage", desc: "Attach receipts, photos, and manuals to any asset." },
    { icon: Package, name: "Data Backups", desc: "Export household data anytime for peace of mind." },
    { icon: Users, name: "Expanded Capacity", desc: "Add more members and households as life expands." },
    { icon: Cat, name: "Pet Care & Tracking", desc: "Vaccinations, medications, and care reminders." },
    { icon: Sparkles, name: "Eva Assistant", desc: "Natural language task and event management." },
    { icon: ShoppingBasket, name: "Advanced Groceries", desc: "Smart categorization and consumption tracking." },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white animate-in fade-in duration-500 transition-colors">
      
      {/* 1. Navigation */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto sticky top-0 bg-slate-50/80 dark:bg-slate-950/80 backdrop-blur-md z-50">
        <div onClick={() => navigate('/')} className="flex items-center gap-2 font-black text-2xl tracking-tighter cursor-pointer group">
          <div className="p-1.5 rounded-xl bg-indigo-600 text-white shadow-lg group-hover:scale-110 transition-transform">
            <Home className="w-6 h-6" />
          </div>
          <span>Everyday<span className="text-indigo-600">OS</span></span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-1 bg-white dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            {['en', 'fr', 'es'].map((lng) => (
              <button 
                key={lng}
                onClick={() => i18n.changeLanguage(lng)} 
                className={`px-3 py-1.5 text-[10px] font-black rounded-lg transition-all ${
                  i18n.language.startsWith(lng) 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>
          <Button onClick={() => navigate('/login')} variant="ghost" className="font-bold rounded-xl">
            {t('nav.login', 'Login')}
          </Button>
        </div>
      </nav>

      {/* 2. Header */}
      <header className="px-8 pt-16 pb-12 text-center max-w-4xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-black tracking-tight mb-6 leading-tight whitespace-pre-line">
          One system. <br /><span className="text-indigo-600">{t('pricing.tailored', 'Tailored to you.')}</span>
        </h1>
        <p className="text-xl text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
          {t('pricing.subtitle', 'Choose a foundation that fits your size, then add the power-ups that matter most to your shared life.')}
        </p>
      </header>

      {/* 3. Core Plans Grid */}
      <main className="px-8 py-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {corePlans.map((plan) => (
            <div key={plan.id} className={`bg-white dark:bg-slate-900 p-8 rounded-[3rem] border-2 flex flex-col relative transition-all hover:shadow-2xl hover:scale-[1.01] ${plan.accent}`}>
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">
                  {t('pricing.popular', 'Most Popular')}
                </div>
              )}
              
              <div className="mb-8">
                <span className="text-4xl mb-4 block">{plan.icon}</span>
                <h3 className="text-2xl font-black mb-1">{plan.name}</h3>
                <p className="text-sm font-bold text-slate-400 mb-6">{plan.tagline}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-black">{plan.price}</span>
                  <span className="text-slate-400 font-bold">/mo</span>
                </div>
              </div>

              <div className="space-y-4 mb-10 flex-1">
                {plan.features.map((f, idx) => (
                  <div key={`${plan.id}-feat-${idx}`} className="flex items-start gap-3 text-sm font-bold text-slate-700 dark:text-slate-300">
                    <span className="shrink-0 text-base">{plan.featureIcon}</span>
                    <span className="leading-tight">{f}</span>
                  </div>
                ))}
                
                {plan.notIncluded.map((f, idx) => (
                  <div key={`${plan.id}-not-${idx}`} className="flex items-start gap-3 text-sm font-bold text-slate-300 dark:text-slate-600 opacity-50">
                    <X className="w-5 h-5 shrink-0" />
                    <span className="leading-tight">{f}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => navigate('/login')} 
                className={`w-full rounded-2xl h-14 font-black text-lg shadow-lg ${
                  plan.recommended 
                    ? 'bg-indigo-600 text-white shadow-indigo-600/20' 
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200'
                }`}
              >
                {plan.buttonText}
              </Button>
            </div>
          ))}
        </div>
      </main>

      {/* 4. Add-ons Section */}
      <section className="px-8 py-24 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <h2 className="text-4xl font-black mb-4">A La Carte Add-ons</h2>
            <p className="text-lg text-slate-500 font-medium">
              Customize your OS by adding specific module intelligence to any plan.
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-900/30">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-black text-amber-700 dark:text-amber-400 uppercase tracking-widest whitespace-nowrap">
              One-click activation
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {addOns.map((add, i) => (
            <div key={i} className="group p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 hover:border-indigo-600 transition-all hover:shadow-xl cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                  <add.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-black text-slate-900 dark:text-white">{add.name}</h4>
                    <Plus className="w-4 h-4 text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-xs font-bold text-slate-500 dark:text-slate-400 leading-relaxed">
                    {add.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Comprehensive Footer */}
      <footer className="bg-slate-50 dark:bg-slate-900 pt-20 pb-10 px-8 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 font-black text-xl tracking-tighter mb-4">
                <Home className="w-5 h-5 text-indigo-600" />
                <span>Everyday<span className="text-indigo-600">OS</span></span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium leading-relaxed">
                The ultimate operating system for modern households. Organize, collaborate, and thrive.
              </p>
            </div>

            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">Product</h4>
              <ul className="space-y-4">
                <li><Link to="/" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Home</Link></li>
                <li><Link to="/pricing" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors underline decoration-indigo-600 decoration-2 underline-offset-4">Pricing</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">Legal</h4>
              <ul className="space-y-4">
                <li><Link to="/privacy" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Terms</Link></li>
                <li><Link to="/disclaimer" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Safety</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">Support</h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                  <Mail className="w-4 h-4 text-indigo-600" /> support@everydayos.com
                </li>
                <li className="flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                  <Globe className="w-4 h-4 text-indigo-600" /> London, UK
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            <p>© 2026 EverydayOS • Systems Operational</p>
          </div>
        </div>
      </footer>
    </div>
  );
};